import type {
  AvailabilityCalendar,
  AvailabilityCustomBlock,
  AvailabilityKind,
  AvailabilityRecurringBlock,
} from './types'

export type ResolvedAvailability = AvailabilityKind | 'unknown'

function normalizeRecurringBlock(block: AvailabilityRecurringBlock): AvailabilityRecurringBlock {
  return {
    ...block,
    dayOfWeekStart: block.dayOfWeekStart,
    dayOfWeekEnd: block.dayOfWeekEnd,
  }
}

function daysBetween(startDay: number, endDay: number): number {
  let diff = endDay - startDay
  if (diff < 0) diff += 7
  return diff
}

function minutesFromTime(hour: number, minute: number): number {
  return hour * 60 + minute
}

function recurringBlockContains(block: AvailabilityRecurringBlock, at: Date): boolean {
  const b = normalizeRecurringBlock(block)
  const day = at.getDay()
  const minuteOfDay = at.getHours() * 60 + at.getMinutes()
  const startMin = minutesFromTime(b.startHour, b.startMinute)
  const endMin = minutesFromTime(b.endHour, b.endMinute)
  const span = daysBetween(b.dayOfWeekStart, b.dayOfWeekEnd)

  if (span === 0) {
    if (day !== b.dayOfWeekStart) return false
    if (endMin <= startMin) return false
    return minuteOfDay >= startMin && minuteOfDay < endMin
  }

  for (let offset = 0; offset <= span; offset++) {
    const blockDay = (b.dayOfWeekStart + offset) % 7
    if (blockDay !== day) continue
    const isFirst = offset === 0
    const isLast = offset === span
    const segStart = isFirst ? startMin : 0
    const segEnd = isLast ? endMin : 24 * 60
    if (minuteOfDay >= segStart && minuteOfDay < segEnd) return true
  }
  return false
}

function customBlockContains(block: AvailabilityCustomBlock, at: Date): boolean {
  const start = Date.parse(block.startTime)
  const end = Date.parse(block.endTime)
  const ms = at.getTime()
  return ms >= start && ms < end
}

function mergeKinds(kinds: AvailabilityKind[]): ResolvedAvailability {
  if (kinds.length === 0) return 'unknown'
  if (kinds.includes('unavailable')) return 'unavailable'
  return 'available'
}

/** Resolve effective availability at an instant. One-offs beat recurring; unavailable beats available. */
export function resolveAvailabilityAt(
  calendar: AvailabilityCalendar,
  at: Date = new Date(),
): ResolvedAvailability {
  const customKinds = calendar.customBlocks
    .filter((b) => customBlockContains(b, at))
    .map((b) => b.kind)
  if (customKinds.length > 0) return mergeKinds(customKinds)

  const recurringKinds = calendar.recurringBlocks
    .filter((b) => recurringBlockContains(b, at))
    .map((b) => b.kind)
  return mergeKinds(recurringKinds)
}
